import { useRef, useState, type MutableRefObject } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { allowOrWarn } from '../../util/Util';
import type { ExpenseRow } from './expenseTypes';
import { canDrop } from '../../util/access';

export const useExpenseDelete = (
  onSuccess: () => void,
  toastIdRef?: MutableRefObject<string | number>,
) => {
  const { api, access } = useAuth();
  const { setLoadingMessage, queueReRun, t } = useUI();
  const [toDelete, setToDelete] = useState<ExpenseRow | null>(null);
  const [toDeleteBulk, setToDeleteBulk] = useState<ExpenseRow[]>([]);
  const tableRef = useRef<any>(null);
  const internalToastIdRef = useRef<string | number>(-1);
  const toastRef = toastIdRef ?? internalToastIdRef;

  const canDelete = canDrop(access, 'expense');
  const canDeleteBulk = canDrop(access, 'expense', 'delete_bulk');

  const handleDelete = async () => {
    if (!toDelete) return;
    setLoadingMessage('DELETE');
    const res = await api.delete(`/expenses/${toDelete.id}`);
    if (res?.status === 204) {
      toastRef.current = toast.loading(
        `${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`,
      );
      onSuccess();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleDelete);
    }
    setLoadingMessage(null);
    setToDelete(null);
  };

  const handleBulkDelete = async () => {
    if (toDeleteBulk.length === 0) return;
    setLoadingMessage('DELETE');
    const res = await api.post('/expenses/delete', {
      ids: toDeleteBulk.map((row) => row.id),
    });
    if (res?.status === 204) {
      toastRef.current = toast.loading(
        `${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`,
      );
      tableRef.current?.resetRowSelection();
      onSuccess();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleBulkDelete);
    }
    setLoadingMessage(null);
    setToDeleteBulk([]);
  };

  const requestRowDelete = (row: ExpenseRow) => {
    if (allowOrWarn(canDrop(access, 'expense'))) {
      setToDelete(row);
    }
  };

  const requestSelectionDelete = (table: any) => {
    if (!allowOrWarn(canDrop(access, 'expense', 'delete_bulk'))) return;
    const rows = table.getSelectedRowModel().flatRows.map((entry: any) => entry.original);
    if (rows.length === 1) {
      setToDelete(rows[0]);
    } else if (rows.length > 1) {
      tableRef.current = table;
      setToDeleteBulk(rows);
    }
  };

  return {
    canDelete,
    canDeleteBulk,
    toDelete,
    toDeleteBulk,
    setToDelete,
    setToDeleteBulk,
    handleDelete,
    handleBulkDelete,
    requestRowDelete,
    requestSelectionDelete,
    isConfirmOpen: Boolean(toDelete) || toDeleteBulk.length > 0,
  };
};
